export type UserState = {
  id: string;
  socketId: string;
  sessionStarted: Date;
  createdRoom: string | null;
  joinedRoom: string | null;
  name: string;
  colour: UserColourOptions;
  character: Character;
}
export type PublicUser = Pick<UserState, 'id' | 'colour' | 'name' | 'character'>;

export type UserUpdatePayload = {
  userId: string;
  name?: string;
  colour?: UserColourOptions;
}

export type UserColourOptions = "red" | "blue" | "yellow" | "green" | "pink" | "purple";

export type Character = {
  slug: CharacterSlugStrings;
  skin: CharacterSkinStrings;
}

export enum CharacterSlugs {
  girl = "char_girl",
  guy = "char_guy",
  red = "char2",
  sunny = "char_sunny"
}
export type CharacterSlugStrings = `${CharacterSlugs}`;
export enum CharacterSkins {
  dark = "dark",
  medium = "medium",
  light = "light"
}
export type CharacterSkinStrings = `${CharacterSkins}`;

export type CharacterDetails = {
  uiHeight: number;
  scale: number;
  previewHeight: number;
  name: string;
}

export type RoomState = {
  code: string;
  creator: string;
  createdAt: Date;
  password?: string;
}

export type InitialConnect = {
  userId: string | undefined;
  preferences?: Pick<UserState, 'character' | 'colour' | 'name'>;
}

export type CreateRoomPayload = {
  userId: string;
  password?: string;
}
export type JoinRoomPayload = {
  userId: string,
  roomCode: string
  password?: string;
}
export type LeaveRoomPayload = {
  userId: string,
  roomCode: string
}
export type DisconnectTimerEndedPayload = {
  roomCode: string;
  userId: string;
  leftUserId: string;
}

export type GameSession = {
  roomCode: string;
  currentDealer: string;
  currentPlayer: string;
  currentRound: number;
  currentState: "round" | "player_decision" | "dealer_decision" | "round_waiting" | "disconnect_waiting" | "starting" | "ending" | "start_waiting";
  deck: Deck;
  error?: "player_missing" | "game_state_corrupt";
  config: GameConfig;
}
export type PublicGameSession = Omit<GameSession, 'deck'> & {
  deck?: never;
}

export type GameConfig = {
  lives: number;
  turnTimer: number; //0 for no timer
}

export type SpectatorState = Omit<PublicUser, 'id'> & {
  userId: string;
  isDisconnected: boolean;
};

export type GamePlayersState = {
  players: GamePlayerInfo[];
  spectators: SpectatorState[];
}
export type PublicGamePlayersState = Omit<GamePlayersState, 'players'> & {
  players: PublicGamePlayerInfo[];
}

export type GamePlayerInfo = Omit<PublicUser, 'id'> & {
  userId: string;
  socketId: string;
  position: number;
  lives: number;
  isBot: boolean;
  isDisconnected: boolean;
  isCreator: boolean;
  card: Card;
  wins: number;
  facing: PlayerFacingDirection;
};
export type PublicGamePlayerInfo = Omit<GamePlayerInfo, 'card'> & {
  card: Card | null;
}

export type Suit = 'h'|'d'|'c'|'s';
export type CardRank = 1|2|3|4|5|6|7|8|9|10|11|12|13;
export type Card = {
  suit: Suit;
  rank: CardRank;
}
export type Deck = Card[];

export enum ClientRoundEvents {
  playerChoice = 'player-choice', //userId string
  dealerChoice = 'dealer-choice', //userId string
  flipAllCards = 'flip-all-cards', //undefined
  tradeCards = 'trade-cards', // to: string, from: string
  blockTrade = 'block-trade', // to: string, from: string
  dealerCutResult = 'dealer-cut-result', //card: Card
  dead = 'dead'
}
export enum ServerRoundEvents {
  playerSwap = 'player-swap',
  playerStay = 'player-stay',
  deal = 'deal',
  dealerCut = 'dealer-cut',
  dealerStay = 'dealer-stay',
  startGame = 'start-game'
}

export type ServerRoundEventPayload = {
  event: `${ServerRoundEvents}`;
  userId: string;
  roomCode: string;
  config?: never;
  data?: number;
}
export type ServerStartGamePayload = Omit<ServerRoundEventPayload, 'config'> & {
  config: GameConfig;
}

export type PlayerAction = 'swap' | 'stay';
export type DealerAction = 'cut' | 'stay';

export type InitialConnectReturn = UserState;
export type UserUpdatedReturn = UserState;
export type RoomExistingReturn = {
  roomCode: string;
}
export type RoomCreatedReturn = {
  room: RoomState;
  players: PublicGamePlayersState;
}
export type GameAlreadyStartedReturn = undefined;
type UserChangedReturn = {
  user: PublicUser;
  room: RoomState;
  game: PublicGameSession | null;
  players: PublicGamePlayersState;
  nextDealer: GamePlayerInfo | null;
}
export type UserJoinedReturn = UserChangedReturn;
export type UserLeftReturn = UserChangedReturn;
export type UserDisconnectedReturn = UserLeftReturn & {
  game: PublicGameSession | null;
  players: PublicGamePlayersState;
};

export type GameStartedReturn = PublicGameSession;
export type GameEndedReturn = PublicGameSession;
export type NewGameRoundReturn = PublicGameSession;
export type GameResetReturn = PublicGamePlayersState;
type RoundEvent<E,T> = {event: E, data: T};
export type UnknownRoundEvent = RoundEvent<`${ClientRoundEvents}`, unknown>;

export type PlayerChoiceEvent = RoundEvent<ClientRoundEvents.playerChoice, GamePlayerInfo> & {
  game: PublicGameSession;
  players: PublicGamePlayersState;
};
export type DealerChoiceEvent = RoundEvent<ClientRoundEvents.dealerChoice, GamePlayerInfo> & {
  game: PublicGameSession;
  players: PublicGamePlayersState;
};
export type TradeCardsEvent = RoundEvent<ClientRoundEvents.tradeCards, {to: string, from: string}> & {
  players: PublicGamePlayersState;
};
export type BlockTradeEvent = RoundEvent<ClientRoundEvents.blockTrade, {to: string, from: string, card: Card}>;

type FlipAllCardsReturn = {
  players: PublicGamePlayersState;
  losers: GamePlayerInfo[];
  tieReset: boolean;
  winner: GamePlayerInfo | null;
  nextDealer: GamePlayerInfo | null;
}
export type FlipAllCardsEvent = RoundEvent<ClientRoundEvents.flipAllCards, FlipAllCardsReturn> & {
  game: PublicGameSession;
};
export type PlayerDeadEvent = RoundEvent<ClientRoundEvents.dead, GamePlayerInfo>;

export type EventLog = {
  event: EventLogEventOptions;
  time: number;
  message: string;
  relevantUsers: PublicUser[];
  relevantCard?: Card;
}

export type PlayerFacingDirection = -1 | 0 | 1;

export type PlayerFacingPayload = {
  roomCode: string;
  userId: string;
  direction: PlayerFacingDirection;
}

export enum Emotes {
  plead='plead',
  cooked='cooked',
  braindead='braindead',
  suicide='suicide',
  evil='evil',
  depressed='depressed',
  joy='joy',
  nerd='nerd'
}
export type PlayerEmoteSlug = `${Emotes}`;

export type PlayerEmotePayload = {
  roomCode: string;
  userId: string;
  emote: PlayerEmoteSlug;
}

export enum EventLogEvents {
  roomCreated='room-created',
  userJoined='user-joined',
  userRejoined='user-rejoined',
  userLeft='user-left',
  userDisconnect='user-disconnect',
  disconnectWaiting='disconnect-waiting',
  userBot='user-bot',
  creatorChange='creator-change',
  newGame='new-game',
  roundStart="round-start",
  nextDealer='next-dealer',
  playerStay='player-stay',
  playerSwap='player-swap',
  swapBlock='swap-block',
  dealerCut='dealer-cut',
  allWayTie='all-way-tie',
  lostLife='lost-life',
  dead='dead',
  winner='winner'
}
export type EventLogEventOptions = `${EventLogEvents}`;